import { isAxiosError } from "axios"
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { toast } from 'sonner'
import { useSWRConfig } from 'swr'
import { canAddLibraryCollectionMembers } from '@digital-twins/shared-types'

import { CollectionAddPicker } from '@/components/library/CollectionAddPicker'
import { CollectionCreateDialog } from '@/components/library/CollectionCreateDialog'
import { CollectionKpiStrip } from '@/components/library/CollectionKpiStrip'
import { CollectionShareDialog } from '@/components/library/CollectionShareDialog'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { Text } from '@/components/ui/Text'
import { useCollectionShareAdapter } from '@/hooks/useCollectionShareAdapter'
import { KnowledgePageShell } from '@/layouts/KnowledgePageShell/KnowledgePageShell'
import { useAuth } from '@/lib/config/auth-provider'
import { ROUTES } from '@/routes/routes'

import { CollectionBackLink, CollectionHeader, collectionDetailCover } from './CollectionHeader'
import { CollectionResourcesArea } from './CollectionResourcesArea'
import { CollectionTopicsArea } from './CollectionTopicsArea'
import { useCollectionFilters } from './useCollectionFilters'
import { useCollectionKpis } from './useCollectionKpis'
import { revalidateLibraryCollection, useLibraryCollection } from './useLibraryCollection'

type AddPickerTab = 'resources' | 'topics'

/** The shell's test id: `knowledge-collection-page` and `knowledge-collection-scroller`. */
const SHELL_TEST_ID = 'knowledge-collection'

/**
 * One Library collection (`/knowledge/collections/:token`), in the Topic
 * page's shell and language: the header, the stat row, then the Resources
 * and Topics areas, inside an `@container/collection` root. The KPI window
 * and the resource filters live in the page URL. The page owns the edit
 * dialog and the add picker. After a write it refetches the detail, KPIs
 * and Topics, and bumps the Resources area's `revision` so its paged reads
 * refetch too (an infinite key holds its filters, so no matcher reaches it,
 * and a remount would not refetch cached pages).
 */
export default function KnowledgeCollectionPage() {
  const { token = null } = useParams<{ token: string }>()
  const [searchParams, setSearchParams] = useSearchParams()

  const [isEditOpen, setIsEditOpen] = useState(false)
  const [addPickerTab, setAddPickerTab] = useState<AddPickerTab | null>(null)
  const [areasRevision, setAreasRevision] = useState(0)
  const [isShareOpen, setIsShareOpen] = useState(false)

  const navigate = useNavigate()
  const { t } = useTranslation('app')
  const { mutate: mutateScoped } = useSWRConfig()
  const { user } = useAuth()
  const shareAdapter = useCollectionShareAdapter(token, user?.organizationToken)
  const collectionFilters = useCollectionFilters({
    searchParams,
    setSearchParams,
  })
  const detail = useLibraryCollection(token)
  const kpis = useCollectionKpis(token, collectionFilters.window)

  const onChanged = () => {
    if (!token) {
      return
    }
    setAreasRevision((revision) => revision + 1)
    void revalidateLibraryCollection(mutateScoped, token)
  }

  const onSaved = () => {
    setIsEditOpen(false)
    onChanged()
  }

  const onDelete = async () => {
    try {
      await detail.remove()
      navigate(ROUTES.knowledge)
    } catch (error) {
      toast.error(t(isAxiosError(error) && error.response?.data?.code === 'brand_collection_requires_brand_management' ? 'creativeLibrary.manageBrandCollection' : 'library.collections.header.deleteFailed'))
    }
  }

  const openAddPicker = (tab: AddPickerTab) => {
    setAddPickerTab(tab)
  }

  // The badge here and in the rail follow a sharing change once the dialog closes.
  const onShareOpenChange = (open: boolean) => {
    setIsShareOpen(open)
    if (!open && token) {
      void revalidateLibraryCollection(mutateScoped, token)
    }
  }

  if (detail.isLoading) {
    return (
      <KnowledgePageShell testId={SHELL_TEST_ID}>
        <div
          className="space-y-5 pt-5 animate-in fade-in fill-mode-both delay-150"
          aria-hidden="true"
          data-testid="collection-loading"
        >
          <Skeleton className="h-5 w-32 rounded-lg border border-card-border bg-background" />
          <Skeleton className="h-12 w-1/2 rounded-lg border border-card-border bg-background" />
          <Skeleton className="h-14 rounded-lg border border-card-border bg-background" />
          <Skeleton className="h-64 rounded-[calc(var(--radius)+8px)] border border-card-border bg-background" />
        </div>
      </KnowledgePageShell>
    )
  }

  const { collection } = detail

  if (!collection) {
    return (
      <KnowledgePageShell testId={SHELL_TEST_ID}>
        <div className="space-y-5 pt-5">
          <CollectionBackLink />
          {detail.isNotFound || !detail.hasError ? (
            <Text size="sm" color="muted" data-testid="collection-not-found">
              {t('library.collections.header.notFound')}
            </Text>
          ) : (
            <div className="space-y-2" data-testid="collection-load-error">
              <Text size="sm" color="muted">
                {t('library.collections.header.loadFailed')}
              </Text>
              <Button
                type="button"
                variant="outline"
                size="sm"
                shape="pill"
                className="h-8 px-3"
                data-testid="collection-load-error-retry"
                onClick={() => void detail.refresh()}
              >
                {t('library.collections.tryAgain')}
              </Button>
            </div>
          )}
        </div>
      </KnowledgePageShell>
    )
  }

  const overlays = (
    <>
      <CollectionCreateDialog
        open={isEditOpen}
        onOpenChange={setIsEditOpen}
        mode="edit"
        entry="edit"
        collection={collection}
        onSaved={onSaved}
      />
      <CollectionAddPicker
        open={addPickerTab != null}
        onOpenChange={(open) => {
          if (!open) {
            setAddPickerTab(null)
          }
        }}
        collection={collection}
        initialTab={addPickerTab ?? 'resources'}
        onChanged={onChanged}
      />
      {shareAdapter && collection.isOwner ? (
        <CollectionShareDialog open={isShareOpen} onOpenChange={onShareOpenChange} adapter={shareAdapter} />
      ) : null}
    </>
  )

  return (
    <KnowledgePageShell testId={SHELL_TEST_ID} overlays={overlays}>
      <div className="@container/collection" data-testid="knowledge-collection-root">
        <CollectionHeader
          collection={collection}
          cover={collectionDetailCover(collection)}
          onAdd={() => openAddPicker('resources')}
          onEdit={() => setIsEditOpen(true)}
          onDelete={() => void onDelete()}
          onShare={shareAdapter ? () => setIsShareOpen(true) : undefined}
        />
        {kpis.hasError && !kpis.kpis ? (
          <div className="flex items-center gap-3" data-testid="collection-kpi-error">
            <Text size="sm" color="muted">
              {t('library.collections.kpis.loadFailed')}
            </Text>
            <Button
              type="button"
              variant="outline"
              size="sm"
              shape="pill"
              data-testid="collection-kpi-error-retry"
              onClick={() => void kpis.refresh()}
            >
              {t('library.collections.tryAgain')}
            </Button>
          </div>
        ) : (
          <CollectionKpiStrip
            preset={collection.preset}
            kpis={kpis.kpis}
            window={collectionFilters.window}
            onWindowChange={collectionFilters.setWindow}
            topicCount={collection.topicCount}
          />
        )}
        <div className="mt-8 space-y-16">
          <CollectionResourcesArea
            collection={collection}
            collectionFilters={collectionFilters}
            onAdd={() => openAddPicker('resources')}
            revision={areasRevision}
            onMembersChanged={onChanged}
          />
          <CollectionTopicsArea
            collectionToken={collection.token}
            collectionName={collection.name}
            canEdit={canAddLibraryCollectionMembers(collection)}
            onAdd={() => openAddPicker('topics')}
          />
        </div>
      </div>
    </KnowledgePageShell>
  )
}
